import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format } from "date-fns";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { api, getToken, clearToken, BUSINESS } from "../lib/api";
import { LanguageSelector } from "../components/LanguageSelector";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { LogOut, ArrowLeft, CalendarDays, Inbox, Check, X, Mail, Loader2, Images, Type, Euro, ListChecks, RefreshCw, CalendarRange, Tag } from "lucide-react";
import { PhotosPanel, TextsPanel, PricingPanel, AmenitiesPanel, SyncPanel, SeasonsPanel, PromosPanel } from "../components/OwnerContentPanels";

const fmt = (d) => format(d, "yyyy-MM-dd");

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

export default function OwnerDashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [requests, setRequests] = useState([]);
  const [busyDate, setBusyDate] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [av, rq] = await Promise.all([api.get("/availability"), api.get("/booking-requests")]);
      setBlocked(av.data.blocked || []);
      setRequests(rq.data || []);
    } catch (e) { /* handled by auth check */ }
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/"); return; }
    api.get("/auth/me")
      .then(() => { setAuthed(true); loadAll(); })
      .catch(() => { clearToken(); navigate("/"); });
  }, [navigate, loadAll]);

  const bookedDates = useMemo(
    () => blocked.map((s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); }),
    [blocked]
  );

  const toggleDate = async (day) => {
    if (busyDate) return;
    setBusyDate(true);
    try {
      await api.post("/availability/toggle", { date: fmt(day) });
      const { data } = await api.get("/availability");
      setBlocked(data.blocked || []);
      toast.success(t("owner.saved"));
    } catch (e) {
      toast.error("Error");
    } finally {
      setBusyDate(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/booking-requests/${id}`, { status });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(t("owner.saved"));
    } catch (e) {
      toast.error("Error");
    }
  };

  const logout = () => { clearToken(); navigate("/"); };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5]">
        <Loader2 className="h-8 w-8 animate-spin text-[#C85A32]" />
      </div>
    );
  }

  const statusLabel = (s) => t(`owner.status${s.charAt(0).toUpperCase() + s.slice(1)}`);

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="sticky top-0 z-40 border-b border-stone-200/70 bg-stone-50/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 py-3.5">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} data-testid="owner-back-btn" className="text-stone-500 hover:text-stone-800">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="font-serif text-xl font-semibold text-stone-900">{t("owner.dashboard")}</h1>
          </div>
          <div className="flex items-center gap-2.5">
            <LanguageSelector />
            <Button data-testid="owner-logout-btn" onClick={logout} variant="outline" size="sm" className="gap-1.5">
              <LogOut className="h-3.5 w-3.5" /> {t("owner.logout")}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <Tabs defaultValue="calendar">
          <TabsList data-testid="owner-dashboard-tabs" className="mb-6 flex h-auto flex-wrap justify-start gap-1">
            <TabsTrigger value="calendar" data-testid="owner-tab-calendar" className="gap-1.5">
              <CalendarDays className="h-4 w-4" /> {t("owner.tabCalendar")}
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="owner-tab-requests" className="gap-1.5">
              <Inbox className="h-4 w-4" /> {t("owner.tabRequests")}
              {requests.filter((r) => r.status === "pending").length > 0 && (
                <span className="ml-1 rounded-full bg-[#C85A32] px-1.5 text-xs text-white">
                  {requests.filter((r) => r.status === "pending").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="photos" data-testid="owner-tab-photos" className="gap-1.5"><Images className="h-4 w-4" /> {t("owner.tabPhotos")}</TabsTrigger>
            <TabsTrigger value="texts" data-testid="owner-tab-texts" className="gap-1.5"><Type className="h-4 w-4" /> {t("owner.tabTexts")}</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="owner-tab-pricing" className="gap-1.5"><Euro className="h-4 w-4" /> {t("owner.tabPricing")}</TabsTrigger>
            <TabsTrigger value="amenities" data-testid="owner-tab-amenities" className="gap-1.5"><ListChecks className="h-4 w-4" /> {t("owner.tabAmenities")}</TabsTrigger>
            <TabsTrigger value="seasons" data-testid="owner-tab-seasons" className="gap-1.5"><CalendarRange className="h-4 w-4" /> {t("owner.tabSeasons")}</TabsTrigger>
            <TabsTrigger value="promos" data-testid="owner-tab-promos" className="gap-1.5"><Tag className="h-4 w-4" /> {t("owner.tabPromos")}</TabsTrigger>
            <TabsTrigger value="sync" data-testid="owner-tab-sync" className="gap-1.5"><RefreshCw className="h-4 w-4" /> {t("owner.tabSync")}</TabsTrigger>
          </TabsList>

          <TabsContent value="calendar">
            <div className="rounded-2xl border border-stone-200/80 bg-white p-6">
              <p className="mb-4 text-sm text-stone-500">{t("owner.calendarHint")}</p>
              <div className="flex flex-col items-start gap-6 lg:flex-row">
                <DayPicker
                  mode="multiple"
                  numberOfMonths={1}
                  selected={bookedDates}
                  onDayClick={toggleDate}
                  modifiers={{ booked: bookedDates }}
                  modifiersClassNames={{ booked: "rdp-day_booked" }}
                  disabled={{ before: new Date() }}
                />
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-2"><i className="h-3 w-3 rounded-sm" style={{ background: "rgba(95,107,82,0.25)" }} />{t("booking.legendAvailable")}</span>
                  <span className="flex items-center gap-2"><i className="h-3 w-3 rounded-sm" style={{ background: "rgba(200,90,50,0.35)" }} />{t("booking.legendBooked")}</span>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="requests">
            <div className="rounded-2xl border border-stone-200/80 bg-white p-2 sm:p-4">
              {requests.length === 0 ? (
                <p className="py-16 text-center text-stone-500" data-testid="requests-empty">{t("owner.requestsEmpty")}</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="requests-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("owner.colGuest")}</TableHead>
                        <TableHead>{t("owner.colDates")}</TableHead>
                        <TableHead>{t("owner.colGuests")}</TableHead>
                        <TableHead>{t("owner.colStatus")}</TableHead>
                        <TableHead className="text-right">{t("owner.colActions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requests.map((r) => (
                        <TableRow key={r.id} data-testid={`request-row-${r.id}`}>
                          <TableCell>
                            <div className="font-medium text-stone-900">{r.name}</div>
                            <div className="text-xs text-stone-500">{r.email}</div>
                            {r.phone && <div className="text-xs text-stone-500">{r.phone}</div>}
                            {r.message && <div className="mt-1 max-w-[220px] text-xs italic text-stone-400">“{r.message}”</div>}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {r.checkin} <span className="text-stone-400">→</span> {r.checkout}
                          </TableCell>
                          <TableCell className="text-center">{r.guests}</TableCell>
                          <TableCell>
                            <Badge variant="outline" data-testid="owner-request-status-badge" className={STATUS_STYLE[r.status]}>
                              {statusLabel(r.status)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1.5">
                              {r.status !== "confirmed" && (
                                <Button size="icon" variant="outline" data-testid="owner-request-action-approve" onClick={() => updateStatus(r.id, "confirmed")} className="h-8 w-8 text-emerald-600 hover:bg-emerald-50" title={t("owner.approve")}>
                                  <Check className="h-4 w-4" />
                                </Button>
                              )}
                              {r.status !== "rejected" && (
                                <Button size="icon" variant="outline" data-testid="owner-request-action-reject" onClick={() => updateStatus(r.id, "rejected")} className="h-8 w-8 text-red-600 hover:bg-red-50" title={t("owner.reject")}>
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                              <a href={`mailto:${r.email}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50" title={t("owner.emailGuest")}>
                                <Mail className="h-4 w-4" />
                              </a>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="photos"><PhotosPanel /></TabsContent>
          <TabsContent value="texts"><TextsPanel /></TabsContent>
          <TabsContent value="pricing"><PricingPanel /></TabsContent>
          <TabsContent value="amenities"><AmenitiesPanel /></TabsContent>
          <TabsContent value="seasons"><SeasonsPanel /></TabsContent>
          <TabsContent value="promos"><PromosPanel /></TabsContent>
          <TabsContent value="sync"><SyncPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
